import React, { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Menu, LogOut, ChevronDown } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { OfflineStatusBanner } from '../offline/OfflineStatusBanner';
import { useAuth, clearTokens } from '../../lib/auth';
import { cn } from '../../lib/utils';
import api from '../../lib/api';

// ─── Site filter store (Zustand) ──────────────────────────────────────────────
import { create } from 'zustand';

interface SiteFilterState {
  selectedSite: string | null;
  setSelectedSite: (site: string | null) => void;
}

export const useSiteFilter = create<SiteFilterState>((set) => ({
  selectedSite: null,
  setSelectedSite: (site) => set({ selectedSite: site }),
}));

// Placeholder site list — replace with API-driven data when available
const SITES = ['All Sites', 'Banjul HQ', 'Kanifing', 'Brikama', 'Serekunda'];

// ─── User avatar fallback ─────────────────────────────────────────────────────
function getInitials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((n) => n[0])
    .join('')
    .toUpperCase();
}

// ─── AppShell ─────────────────────────────────────────────────────────────────
export function AppShell() {
  const { user, clearUser, isOfflineSession } = useAuth();
  const queryClient = useQueryClient();
  const { selectedSite, setSelectedSite } = useSiteFilter();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!user || isOfflineSession) return;
    const heartbeat = () => {
      if (document.visibilityState !== 'visible' || !navigator.onLine) return;
      void api.post('/presence/heartbeat').then(() => {
        void queryClient.invalidateQueries({ queryKey: ['presence-online'] });
      }).catch(() => { /* Presence is optional when the network is unavailable. */ });
    };
    heartbeat();
    const interval = window.setInterval(heartbeat, 45_000);
    document.addEventListener('visibilitychange', heartbeat);
    window.addEventListener('online', heartbeat);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', heartbeat);
      window.removeEventListener('online', heartbeat);
    };
  }, [user?.id, isOfflineSession, queryClient]);

  function handleLogout() {
    clearTokens();
    clearUser();
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* ── Mobile sidebar overlay ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* ── Sidebar (fixed on desktop, drawer on mobile) ── */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex-shrink-0 transition-transform duration-200 lg:static lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <Sidebar
          role={user?.role}
          isOfflineSession={isOfflineSession}
          onClose={() => setSidebarOpen(false)}
        />
      </div>

      {/* ── Main area ── */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Top bar */}
        <header className="flex h-14 items-center justify-between border-b border-border bg-card px-4 shadow-sm">
          {/* Left: hamburger (mobile) + logo (desktop) */}
          <div className="flex items-center gap-3">
            <button
              className="rounded p-1.5 text-muted-foreground hover:bg-muted lg:hidden"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open sidebar"
            >
              <Menu className="h-5 w-5" />
            </button>
            <img
              src="/logo.jpg"
              alt="Hold Engineering"
              className="hidden h-8 w-auto object-contain lg:block"
            />
          </div>

          {/* Centre: site filter */}
          <div className="flex items-center gap-2">
            <label htmlFor="site-filter" className="text-xs font-medium text-muted-foreground hidden sm:block">
              Site:
            </label>
            <div className="relative">
              <select
                id="site-filter"
                value={selectedSite ?? ''}
                onChange={(e) => setSelectedSite(e.target.value || null)}
                className="appearance-none rounded-md border border-input bg-background py-1.5 pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">All Sites</option>
                {SITES.filter((s) => s !== 'All Sites').map((site) => (
                  <option key={site} value={site}>
                    {site}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>

          {/* Right: user info + logout */}
          <div className="flex items-center gap-3">
            {user && (
              <>
                <div className="flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    {user.photoUrl ? (
                      <img
                        src={user.photoUrl}
                        alt={user.fullName}
                        className="h-full w-full object-cover object-top"
                      />
                    ) : (
                      getInitials(user.fullName)
                    )}
                  </div>
                  <span className="hidden text-sm font-medium text-foreground sm:block">
                    {user.fullName}
                  </span>
                </div>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                  aria-label="Log out"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:block">Logout</span>
                </button>
              </>
            )}
          </div>
        </header>

        <OfflineStatusBanner />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
